// PhilosophyFlowDesktopScene.js - Full-page desktop Philosophy Flow
// Adapted from mobile version with desktop-optimized UI/UX

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db, firebase } from "../firebaseConfig.js";
import claudeAPIService from "../services/claudeAPIService.js";
import { hasSpecialAccess } from '../services/specialAccessService.js';
import { createTemplateHeader, loadTemplateHeaderCSS, bindTemplateHeaderEvents } from "../components/SharedTemplateHeader.js";
import { createSidebarNavigation, getSceneKeyForSidebar } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

// Desktop-optimized constants (copied from mobile)
const PHILOSOPHY_FLOW_STEPS = {
    CATEGORY_SELECTION: 1,
    EMOTION_SELECTION: 2,
    COLOR_SELECTION: 3,
    SLEEP_SELECTION: 4,
    GENERATING: 5,
    DISPLAY_QUOTE: 6,
    RATE_LIMITED: 7
};

const EMOTION_CATEGORIES = {
    'positive-high': [
        { emoji: '🥳', label: 'Excited', description: 'Feeling energized and enthusiastic' },
        { emoji: '🤩', label: 'Inspired', description: 'Feeling motivated and creative' },
        { emoji: '😎', label: 'Confident', description: 'Feeling assured and capable' },
        { emoji: '🔥', label: 'Energized', description: 'Feeling powerful and driven' }
    ],
    'positive-calm': [
        { emoji: '😌', label: 'Peaceful', description: 'Feeling calm and centered' },
        { emoji: '😊', label: 'Content', description: 'Feeling satisfied and happy' },
        { emoji: '🤔', label: 'Thoughtful', description: 'Feeling reflective and contemplative' },
        { emoji: '😇', label: 'Grateful', description: 'Feeling appreciative and blessed' }
    ],
    'challenging': [
        { emoji: '😟', label: 'Worried', description: 'Feeling concerned about outcomes' },
        { emoji: '😢', label: 'Sad', description: 'Feeling down or melancholic' },
        { emoji: '😨', label: 'Anxious', description: 'Feeling nervous or uncertain' },
        { emoji: '😤', label: 'Frustrated', description: 'Feeling blocked or hindered' }
    ],
    'intense': [
        { emoji: '😠', label: 'Angry', description: 'Feeling upset or irritated' },
        { emoji: '😡', label: 'Furious', description: 'Feeling intense anger' },
        { emoji: '🤯', label: 'Overwhelmed', description: 'Feeling too much at once' },
        { emoji: '😱', label: 'Shocked', description: 'Feeling surprised or stunned' }
    ]
};

const EXECUTIVE_COLOR_PALETTE = [
    { hex: '#FF6B6B', name: 'Executive Red', mood: 'Power' },
    { hex: '#FFA500', name: 'Strategic Orange', mood: 'Innovation' },
    { hex: '#FFD700', name: 'Leadership Gold', mood: 'Excellence' },
    { hex: '#98D8C8', name: 'Clarity Mint', mood: 'Focus' },
    { hex: '#00D4FF', name: 'DeckOps Blue', mood: 'Intelligence' },
    { hex: '#8B5CF6', name: 'Visionary Purple', mood: 'Insight' },
    { hex: '#10B981', name: 'Growth Green', mood: 'Progress' },
    { hex: '#EC4899', name: 'Creative Pink', mood: 'Innovation' },
    { hex: '#F59E0B', name: 'Ambition Amber', mood: 'Drive' },
    { hex: '#6366F1', name: 'Trust Indigo', mood: 'Stability' },
    { hex: '#14B8A6', name: 'Balance Teal', mood: 'Harmony' },
    { hex: '#F43F5E', name: 'Passion Rose', mood: 'Energy' }
];

export default class PhilosophyFlowDesktopScene extends BaseMenuScene {
    constructor() {
        super({ key: "PhilosophyFlowDesktopScene" });
        this.domElement = null;
        this.currentStep = PHILOSOPHY_FLOW_STEPS.CATEGORY_SELECTION;
        this.sessionData = {
            selectedCategory: null,
            selectedEmotion: null,
            selectedColor: null,
            selectedSleep: null,
            quoteData: null,
            sessionId: this._generateSessionId()
        };
        this.isAdmin = false;
        this.isRateLimited = false;
        this.claudeService = new claudeAPIService();
        // Journal/notes functionality
        this.executiveNote = '';
        this.isFavorited = false;
        this.isSaved = false;
        this.selectedTags = []; // User-selected tags for current quote
        this.availableTags = []; // User's existing tags from Firebase
    }

    async create() {
        console.log('🖥️ PhilosophyFlowDesktopScene: Creating full-page desktop interface');
        
        // Call parent create first
        super.create();
        
        // Prevent scene transition errors (check if events exist)
        if (this.scene && this.scene.events) {
            this.scene.events.on('shutdown', () => {
                console.log('🖥️ PhilosophyFlowDesktopScene: Shutdown event received');
                this.cleanup();
            });
        }
        
        // Load user state
        await this._loadUserState();
        
        // Load template CSS
        loadTemplateHeaderCSS();
        
        // Load EXACT same CSS as Dashboard for consistent sidebar
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        // Load Philosophy Flow specific content CSS
        if (!document.getElementById('philosophy-flow-desktop-css')) {
            const link = document.createElement('link');
            link.id = 'philosophy-flow-desktop-css';
            link.rel = 'stylesheet';
            link.href = '/philosophy-flow-template.css'; // NO query parameter
            document.head.appendChild(link);
        }
        
        // Create the full-page interface
        this.createDesktopInterface();
        
        // Initialize with enterprise services for centralized navigation
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay', // Same ID as other templates for consistent CSS
            bindEvents: true, // Let UnifiedEventBindingSystem handle navigation buttons
            enableNavigation: true // Enable navigation handler integration
        });
        
        // Bind template header events (dashboard navigation)
        bindTemplateHeaderEvents(this);
        
        // Bind Philosophy Flow specific events (on top of centralized navigation)
        this.setupEventListeners();
        
        // Register with overlay manager
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'PhilosophyFlowDesktopScene');
        
        // Signal scene is ready
        this.sceneReady = true;
        
        // Force scene readiness for SceneManager - remove problematic calls
        // this.scene may not be fully initialized yet
        
        console.log('🖥️ PhilosophyFlowDesktopScene: Scene ready signaled');
    }

    createDesktopInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay'; // Use SAME ID as other template scenes for consistent CSS
        overlay.className = 'template-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
        `;
        
        overlay.innerHTML = this.createDesktopHTML();
        
        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        console.log('🖥️ PhilosophyFlowDesktopScene: Full-page interface created');
    }

    createDesktopHTML() {
        const user = auth.currentUser;
        const initials = this.getInitials();
        const userName = user?.displayName || user?.email || 'Executive';
        
        // Use shared sidebar component for 100% consistency
        const sidebar = createSidebarNavigation({
            activeScene: 'philosophy-flow',
            userInitials: initials,
            userName: userName,
            userRole: 'In Session'
        });
        
        return `
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- Layout Container (SAME as Dashboard) -->
            <div class="app-container">
                <!-- Sidebar Navigation (Shared Component - Single Source of Truth) -->
                ${sidebar}
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header with breadcrumb -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">Philosophy Flow</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Training</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Philosophy Flow</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <div class="session-progress" style="
                                background: rgba(255, 255, 255, 0.05);
                                border: 1px solid rgba(255, 255, 255, 0.1);
                                border-radius: 8px;
                                padding: 8px 16px;
                                font-size: 13px;
                                color: rgba(255, 255, 255, 0.8);
                            ">
                                ${this.currentStep <= 4 ? `Step ${this.currentStep} of 4` : this.currentStep === 5 ? 'Generating...' : 'Complete'}
                            </div>
                        </div>
                    </header>
                    
                    <!-- Philosophy Flow Content -->
                    <div class="dashboard-content" style="
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        padding: 48px;
                    ">
                        <div id="philosophy-step-content" style="width: 100%; max-width: 800px;">
                            ${this.getStepContent()}
                        </div>
                    </div>
                </main>
            </div>
        `;
    }
    
    getInitials() {
        const user = auth.currentUser;
        if (user && user.displayName) {
            return user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        }
        return 'EX';
    }

    getStepContent() {
        switch (this.currentStep) {
            case PHILOSOPHY_FLOW_STEPS.CATEGORY_SELECTION:
                return this.createCategorySelectionHTML();
            case PHILOSOPHY_FLOW_STEPS.EMOTION_SELECTION:
                return this.createEmotionSelectionHTML();
            case PHILOSOPHY_FLOW_STEPS.COLOR_SELECTION:
                return this.createColorSelectionHTML();
            case PHILOSOPHY_FLOW_STEPS.SLEEP_SELECTION:
                return this.createSleepSelectionHTML();
            case PHILOSOPHY_FLOW_STEPS.GENERATING:
                return this.createGeneratingHTML();
            case PHILOSOPHY_FLOW_STEPS.DISPLAY_QUOTE:
                return this.createQuoteDisplayHTML();
            case PHILOSOPHY_FLOW_STEPS.RATE_LIMITED:
                return this.createRateLimitedHTML();
            default:
                return this.createCategorySelectionHTML();
        }
    }

    createCategorySelectionHTML() {
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Explore Your Inner Landscape</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    line-height: 1.6;
                ">Choose the category that best represents your current emotional territory</p>
                
                <div class="category-grid" style="
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 24px;
                    max-width: 600px;
                    margin: 0 auto;
                ">
                    <div class="category-card" data-category="positive-high" style="
                        background: rgba(30, 41, 59, 0.8);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 20px;
                        padding: 32px;
                        cursor: pointer;
                        transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                        text-align: center;
                    ">
                        <div style="font-size: 48px; margin-bottom: 16px;">✨</div>
                        <h3 style="font-size: 20px; font-weight: 600; margin-bottom: 12px;">Uplifting States</h3>
                        <p style="font-size: 14px; color: rgba(255, 255, 255, 0.7); line-height: 1.5;">Positive emotions and elevated feelings</p>
                    </div>
                    
                    <div class="category-card" data-category="positive-calm" style="
                        background: rgba(30, 41, 59, 0.8);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 20px;
                        padding: 32px;
                        cursor: pointer;
                        transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                        text-align: center;
                    ">
                        <div style="font-size: 48px; margin-bottom: 16px;">🧠</div>
                        <h3 style="font-size: 20px; font-weight: 600; margin-bottom: 12px;">Cognitive States</h3>
                        <p style="font-size: 14px; color: rgba(255, 255, 255, 0.7); line-height: 1.5;">Mental clarity and thoughtful reflection</p>
                    </div>
                    
                    <div class="category-card" data-category="challenging" style="
                        background: rgba(30, 41, 59, 0.8);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 20px;
                        padding: 32px;
                        cursor: pointer;
                        transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                        text-align: center;
                    ">
                        <div style="font-size: 48px; margin-bottom: 16px;">⚡</div>
                        <h3 style="font-size: 20px; font-weight: 600; margin-bottom: 12px;">Challenging States</h3>
                        <p style="font-size: 14px; color: rgba(255, 255, 255, 0.7); line-height: 1.5;">Difficult emotions requiring attention</p>
                    </div>
                    
                    <div class="category-card" data-category="intense" style="
                        background: rgba(30, 41, 59, 0.8);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 20px;
                        padding: 32px;
                        cursor: pointer;
                        transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                        text-align: center;
                    ">
                        <div style="font-size: 48px; margin-bottom: 16px;">🌊</div>
                        <h3 style="font-size: 20px; font-weight: 600; margin-bottom: 12px;">Intense States</h3>
                        <p style="font-size: 14px; color: rgba(255, 255, 255, 0.7); line-height: 1.5;">Deep emotions requiring gentle attention</p>
                    </div>
                </div>
            </div>
        `;
    }

    createEmotionSelectionHTML() {
        const emotions = EMOTION_CATEGORIES[this.sessionData.selectedCategory] || [];
        
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Now, be more specific</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    line-height: 1.6;
                ">Select the emotion that most accurately represents your feeling</p>
                
                <div class="emotion-grid" style="
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 24px;
                    max-width: 600px;
                    margin: 0 auto 48px auto;
                ">
                    ${emotions.map(emotion => `
                        <div class="emotion-card" data-emotion="${emotion.label}" data-emoji="${emotion.emoji}" data-description="${emotion.description}" style="
                            background: rgba(30, 41, 59, 0.8);
                            backdrop-filter: blur(20px);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 20px;
                            padding: 32px;
                            cursor: pointer;
                            transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                            text-align: center;
                        ">
                            <div style="font-size: 48px; margin-bottom: 16px;">${emotion.emoji}</div>
                            <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 8px;">${emotion.label}</h3>
                            <p style="font-size: 12px; color: rgba(255, 255, 255, 0.6); line-height: 1.4;">${emotion.description}</p>
                        </div>
                    `).join('')}
                </div>
                
                <button class="btn-back" data-action="back" style="
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: rgba(255, 255, 255, 0.9);
                    padding: 12px 24px;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                ">← Back to Categories</button>
            </div>
        `;
    }

    createColorSelectionHTML() {
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Choose Your Energy Color</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    line-height: 1.6;
                ">Select a color that resonates with your emotional state</p>
                
                <div class="color-palette" style="
                    display: grid;
                    grid-template-columns: repeat(6, 1fr);
                    gap: 16px;
                    max-width: 600px;
                    margin: 0 auto 48px auto;
                ">
                    ${EXECUTIVE_COLOR_PALETTE.map(color => `
                        <div class="color-swatch" 
                             data-color="${color.hex}"
                             data-name="${color.name}" 
                             data-mood="${color.mood}"
                             style="
                                 aspect-ratio: 1;
                                 background: ${color.hex};
                                 border-radius: 12px;
                                 border: 3px solid transparent;
                                 cursor: pointer;
                                 transition: all 0.3s ease;
                                 box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                             "
                             title="${color.name} - ${color.mood}">
                        </div>
                    `).join('')}
                </div>
                
                <div class="color-preview" id="colorPreview" style="
                    background: rgba(30, 41, 59, 0.8);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: 32px;
                    margin-bottom: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 24px;
                    max-width: 400px;
                    margin-left: auto;
                    margin-right: auto;
                ">
                    <div id="selectedColorSwatch" style="
                        width: 64px;
                        height: 64px;
                        border-radius: 12px;
                        background: #00d4ff;
                        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
                    "></div>
                    <div>
                        <div id="colorName" style="font-size: 18px; font-weight: 600; margin-bottom: 4px;">Select a color</div>
                        <div id="colorMood" style="font-size: 14px; color: rgba(255, 255, 255, 0.6);">Choose one that represents your energy</div>
                    </div>
                </div>
                
                <div style="display: flex; gap: 16px; justify-content: center;">
                    <button class="btn-back" data-action="back" style="
                        background: rgba(255, 255, 255, 0.1);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                        color: rgba(255, 255, 255, 0.9);
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                    ">← Back</button>
                    
                    <button class="btn-continue" data-action="generate" disabled style="
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        color: #0f172a;
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                        opacity: 0.5;
                    ">Generate Insight →</button>
                </div>
            </div>
        `;
    }

    createGeneratingHTML() {
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Generating Your Insight</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                ">Creating a personalized philosophical reflection...</p>
                
                <div style="margin: 60px 0;">
                    <div style="font-size: 72px; animation: spin 2s linear infinite;">🧠</div>
                    <div style="margin-top: 20px; color: rgba(255, 255, 255, 0.6); font-size: 16px;">
                        Analyzing your emotional state and preferences...
                    </div>
                </div>
            </div>
            
            <style>
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            </style>
        `;
    }

    createQuoteDisplayHTML() {
        const quoteData = this.sessionData.quoteData;
        if (!quoteData) return this.createGeneratingHTML();
        
        // Ensure we have valid data
        const safeQuote = quoteData.quote || "The obstacle is the way.";
        const safeAuthor = quoteData.author || "Marcus Aurelius";
        const safeContext = quoteData.context || "Universal wisdom for strategic thinking.";
        
        return `
            <div class="step-content" style="text-align: center; width: 100%; max-width: 800px; margin: 0 auto;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Your Philosophical Insight</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                ">Based on your emotional intelligence assessment</p>
                
                <div class="quote-card" style="
                    background: linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(0, 212, 255, 0.05));
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 24px;
                    padding: 48px;
                    margin: 0 auto 48px auto;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
                    max-width: 700px;
                    text-align: left;
                ">
                    <div class="quote-text" style="
                        font-size: 24px;
                        line-height: 1.6;
                        font-weight: 500;
                        margin-bottom: 32px;
                        color: rgba(255, 255, 255, 0.9);
                        font-style: italic;
                    ">
                        "${safeQuote}"
                    </div>
                    <div class="quote-author" style="
                        font-size: 18px;
                        font-weight: 600;
                        color: #00d4ff;
                        margin-bottom: 8px;
                    ">
                        — ${safeAuthor}
                    </div>
                    <div class="quote-context" style="
                        font-size: 16px;
                        color: rgba(255, 255, 255, 0.6);
                        line-height: 1.5;
                    ">
                        ${safeContext}
                    </div>
                </div>

                <!-- Action Cards -->
                <div class="action-row" style="
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    max-width: 700px;
                    margin: 0 auto 32px auto;
                ">
                    <div class="action-card" id="favoriteAction" style="
                        background: rgba(30, 41, 59, 0.6);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 12px;
                        padding: 20px;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.3s ease;
                    ">
                        <span class="action-icon" style="font-size: 32px; display: block; margin-bottom: 8px;">⭐</span>
                        <div class="action-label" style="font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.9);">Favorite</div>
                        <div class="action-description" style="font-size: 12px; color: rgba(255, 255, 255, 0.6); margin-top: 4px;">Mark as strategic wisdom</div>
                    </div>
                    
                    <div class="action-card" id="journalAction" style="
                        background: rgba(30, 41, 59, 0.6);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 12px;
                        padding: 20px;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.3s ease;
                    ">
                        <span class="action-icon" style="font-size: 32px; display: block; margin-bottom: 8px;">📋</span>
                        <div class="action-label" style="font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.9);">Executive Notes</div>
                        <div class="action-description" style="font-size: 12px; color: rgba(255, 255, 255, 0.6); margin-top: 4px;">Document insights</div>
                    </div>
                    
                    <div class="action-card" id="saveAction" style="
                        background: rgba(30, 41, 59, 0.6);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 12px;
                        padding: 20px;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.3s ease;
                    ">
                        <span class="action-icon" style="font-size: 32px; display: block; margin-bottom: 8px;">💾</span>
                        <div class="action-label" style="font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.9);">Save to Library</div>
                        <div class="action-description" style="font-size: 12px; color: rgba(255, 255, 255, 0.6); margin-top: 4px;">Archive wisdom</div>
                    </div>
                </div>
                
                <!-- Journal Interface (Hidden by default) -->
                <div class="journal-interface" id="journalInterface" style="
                    background: rgba(30, 41, 59, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 16px;
                    padding: 32px;
                    max-width: 700px;
                    margin: 0 auto 32px auto;
                    display: none;
                    opacity: 0;
                    transition: all 0.3s ease;
                ">
                    <div class="journal-header" style="text-align: center; margin-bottom: 24px;">
                        <div class="journal-title" style="font-size: 20px; font-weight: 600; margin-bottom: 8px; color: rgba(255, 255, 255, 0.9);">Executive Reflection</div>
                        <div class="journal-prompt" style="font-size: 14px; color: rgba(255, 255, 255, 0.7); font-style: italic;">How will you apply this wisdom to your strategic initiatives?</div>
                    </div>
                    <textarea class="journal-input" id="executiveJournal" placeholder="Document your strategic insights, implementation plans, or how this wisdom relates to current initiatives..." maxlength="500" style="
                        width: 100%;
                        min-height: 150px;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.15);
                        border-radius: 12px;
                        padding: 16px;
                        color: rgba(255, 255, 255, 0.9);
                        font-size: 16px;
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        resize: vertical;
                        margin-bottom: 16px;
                    "></textarea>
                    
                    <!-- Tags Section -->
                    <div class="tags-section" style="margin-bottom: 16px;">
                        <div class="tags-label" style="font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.7); margin-bottom: 8px;">
                            🏷️ Tags (optional - for organizing your library)
                        </div>
                        <div id="selectedTags" class="selected-tags" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; min-height: 32px;">
                            <!-- Selected tags will appear here as badges -->
                        </div>
                        <div style="position: relative;">
                            <input type="text" id="tagInput" placeholder="Type to add tags... (e.g., Leadership, Strategy)" style="
                                width: 100%;
                                background: rgba(255, 255, 255, 0.05);
                                border: 1px solid rgba(255, 255, 255, 0.15);
                                border-radius: 8px;
                                padding: 8px 12px;
                                color: rgba(255, 255, 255, 0.9);
                                font-size: 14px;
                                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                            ">
                            <div id="tagSuggestions" class="tag-suggestions" style="
                                position: absolute;
                                top: 100%;
                                left: 0;
                                right: 0;
                                background: rgba(30, 41, 59, 0.95);
                                border: 1px solid rgba(0, 212, 255, 0.3);
                                border-radius: 8px;
                                margin-top: 4px;
                                max-height: 150px;
                                overflow-y: auto;
                                display: none;
                                z-index: 1000;
                                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
                            "></div>
                        </div>
                        <div style="font-size: 12px; color: #00d4ff; margin-top: 6px; font-weight: 600; background: rgba(0, 212, 255, 0.1); padding: 8px; border-radius: 6px; border: 1px solid rgba(0, 212, 255, 0.3);">
                            ⌨️ Press ENTER to add tag | Click suggestions to select
                        </div>
                    </div>
                    
                    <div class="journal-controls" style="display: flex; justify-content: space-between; align-items: center;">
                        <div class="character-count" style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">
                            <span id="charCount">0</span>/500 characters
                        </div>
                        <div class="journal-buttons" style="display: flex; gap: 12px;">
                            <button class="btn-secondary" id="cancelJournal" style="
                                background: rgba(255, 255, 255, 0.1);
                                border: 1px solid rgba(255, 255, 255, 0.2);
                                color: rgba(255, 255, 255, 0.9);
                                padding: 10px 20px;
                                border-radius: 8px;
                                cursor: pointer;
                                font-size: 14px;
                                font-weight: 600;
                            ">Cancel</button>
                            <button class="btn-primary" id="saveJournal" style="
                                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                                border: none;
                                color: #0f172a;
                                padding: 10px 20px;
                                border-radius: 8px;
                                cursor: pointer;
                                font-size: 14px;
                                font-weight: 600;
                            ">Save Strategic Note</button>
                        </div>
                    </div>
                </div>
                
                <!-- Session Actions -->
                <div style="display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;">
                    <button class="btn-secondary" id="newSessionBtn" style="
                        background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.3));
                        border: 1px solid rgba(168, 85, 247, 0.4);
                        color: #a855f7;
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                    ">✨ New Quote Session</button>
                    
                    <button class="btn-secondary" id="discardSession" style="
                        background: rgba(255, 255, 255, 0.1);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                        color: rgba(255, 255, 255, 0.9);
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                    ">Discard Session</button>
                    
                    <button class="btn-primary" id="completeSession" style="
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        color: #0f172a;
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                    ">Complete & Save Session</button>
                    
                    <button class="btn-primary" data-action="dashboard" style="
                        background: rgba(16, 185, 129, 0.2);
                        border: 1px solid rgba(16, 185, 129, 0.3);
                        color: #10b981;
                        padding: 12px 24px;
                        border-radius: 10px;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 600;
                    ">Return to Dashboard</button>
                </div>
                
                <style>
                    .action-card:hover {
                        transform: translateY(-2px);
                        border-color: #00d4ff;
                        box-shadow: 0 8px 24px rgba(0, 212, 255, 0.2);
                    }
                    
                    .action-card.active {
                        background: rgba(0, 212, 255, 0.15);
                        border-color: #00d4ff;
                    }
                    
                    .journal-interface.active {
                        display: block !important;
                        opacity: 1 !important;
                    }
                    
                    .journal-input:focus {
                        outline: none;
                        border-color: #00d4ff;
                        background: rgba(255, 255, 255, 0.08);
                        box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
                    }
                    
                    .btn-secondary:hover, .btn-primary:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                    }
                </style>
            </div>
        `;
    }

    createRateLimitedHTML() {
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #f59e0b 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Daily Limit Reached</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                ">You've completed your daily Philosophy Flow session</p>
                
                <div style="margin: 60px 0;">
                    <div style="font-size: 72px; margin-bottom: 20px;">⏰</div>
                    <p style="color: rgba(255, 255, 255, 0.6); font-size: 18px;">
                        Come back tomorrow for your next philosophical insight.
                    </p>
                </div>
                
                <button class="btn-primary" data-action="dashboard" style="
                    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                    border: none;
                    color: #0f172a;
                    padding: 16px 32px;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: 600;
                ">Return to Dashboard</button>
            </div>
        `;
    }

    setupEventListeners() {
        if (!this.domElement) return;
        
        // Category selection
        this.domElement.querySelectorAll('.category-card').forEach(card => {
            card.addEventListener('click', (e) => {
                const category = e.currentTarget.dataset.category;
                this.selectCategory(category);
            });
            
            // Hover effects
            card.addEventListener('mouseenter', (e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = '#00d4ff';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 212, 255, 0.2)';
            });
            
            card.addEventListener('mouseleave', (e) => {
                if (!e.currentTarget.classList.contains('selected')) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.boxShadow = 'none';
                }
            });
        });
        
        // Emotion selection
        this.domElement.querySelectorAll('.emotion-card').forEach(card => {
            card.addEventListener('click', (e) => {
                const emotionLabel = e.currentTarget.dataset.emotion;
                const emotionEmoji = e.currentTarget.dataset.emoji;
                const emotionDescription = e.currentTarget.dataset.description;
                // Create full emotion object
                const emotionObj = {
                    label: emotionLabel,
                    emoji: emotionEmoji,
                    description: emotionDescription
                };
                this.selectEmotion(emotionObj);
            });
        });
        
        // Color selection
        this.domElement.querySelectorAll('.color-swatch').forEach(swatch => {
            swatch.addEventListener('click', (e) => {
                const colorData = {
                    hex: e.currentTarget.dataset.color,
                    name: e.currentTarget.dataset.name,
                    mood: e.currentTarget.dataset.mood
                };
                this.selectColor(colorData);
            });
        });
        
        // Sleep selection
        this.domElement.querySelectorAll('.sleep-option').forEach(option => {
            option.addEventListener('click', (e) => {
                const sleepHours = e.currentTarget.dataset.sleep;
                // Highlight selected
                document.querySelectorAll('.sleep-option').forEach(opt => {
                    opt.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    opt.style.background = 'rgba(30, 41, 59, 0.8)';
                });
                e.currentTarget.style.borderColor = '#00d4ff';
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.15)';
                this.selectSleep(sleepHours);
            });
        });
        
        // Action buttons
        this.domElement.querySelectorAll('[data-action]').forEach(button => {
            button.addEventListener('click', (e) => {
                const action = e.currentTarget.dataset.action;
                this.handleAction(action);
            });
        });
        
        // Journal/Notes action cards
        const favoriteAction = document.getElementById('favoriteAction');
        if (favoriteAction) {
            favoriteAction.addEventListener('click', () => this.toggleFavorite());
        }
        
        const journalAction = document.getElementById('journalAction');
        if (journalAction) {
            journalAction.addEventListener('click', () => this.openExecutiveJournal());
        }
        
        const saveAction = document.getElementById('saveAction');
        if (saveAction) {
            saveAction.addEventListener('click', () => this.saveToLibrary());
        }
        
        // Journal controls
        const journalInput = document.getElementById('executiveJournal');
        if (journalInput) {
            journalInput.addEventListener('input', () => this.updateCharacterCount());
        }
        
        const cancelJournal = document.getElementById('cancelJournal');
        if (cancelJournal) {
            cancelJournal.addEventListener('click', () => this.cancelJournal());
        }
        
        const saveJournal = document.getElementById('saveJournal');
        if (saveJournal) {
            saveJournal.addEventListener('click', () => this.saveJournal());
        }
        
        // Session actions
        const discardSession = document.getElementById('discardSession');
        if (discardSession) {
            discardSession.addEventListener('click', () => this.discardSession());
        }
        
        const completeSession = document.getElementById('completeSession');
        if (completeSession) {
            completeSession.addEventListener('click', () => this.completeSession());
        }
        
        // New session button
        const newSessionBtn = document.getElementById('newSessionBtn');
        if (newSessionBtn) {
            newSessionBtn.addEventListener('click', () => this.startNewSession());
        }
        
        // NOTE: Tag input listeners are setup when journal opens (in openExecutiveJournal)
        // because the tag input is hidden by default
    }
    
    addTag(tagName) {
        if (!tagName || tagName.length === 0) return;
        if (this.selectedTags.includes(tagName)) return; // Prevent duplicates
        if (this.selectedTags.length >= 5) {
            alert('Maximum 5 tags per quote');
            return;
        }
        
        this.selectedTags.push(tagName);
        this.renderSelectedTags();
        console.log(`🏷️ Tag added: ${tagName}`);
    }
    
    removeTag(tagName) {
        this.selectedTags = this.selectedTags.filter(t => t !== tagName);
        this.renderSelectedTags();
        console.log(`🏷️ Tag removed: ${tagName}`);
    }
    
    renderSelectedTags() {
        const container = document.getElementById('selectedTags');
        if (!container) return;
        
        container.innerHTML = this.selectedTags.map(tag => `
            <span class="tag-badge" style="
                background: linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(14, 165, 233, 0.2));
                border: 1px solid rgba(0, 212, 255, 0.4);
                color: #00d4ff;
                padding: 6px 12px;
                border-radius: 16px;
                font-size: 12px;
                font-weight: 600;
                display: inline-flex;
                align-items: center;
                gap: 6px;
                cursor: default;
            ">
                ${tag}
                <span onclick="window.currentPhilosophyScene?.removeTag('${tag}')" style="
                    cursor: pointer;
                    opacity: 0.7;
                    font-size: 14px;
                    line-height: 1;
                ">×</span>
            </span>
        `).join('');
        
        // Store reference for removeTag to work
        window.currentPhilosophyScene = this;
    }
    
    showTagSuggestions(searchText) {
        const suggestionsDiv = document.getElementById('tagSuggestions');
        if (!suggestionsDiv) return;
        
        if (!searchText || searchText.length < 1) {
            suggestionsDiv.style.display = 'none';
            return;
        }
        
        // Filter existing tags that match the search
        const matches = this.availableTags.filter(tag => 
            tag.toLowerCase().includes(searchText.toLowerCase()) &&
            !this.selectedTags.includes(tag)
        );
        
        if (matches.length === 0) {
            suggestionsDiv.style.display = 'none';
            return;
        }
        
        suggestionsDiv.innerHTML = matches.map(tag => `
            <div class="tag-suggestion-item" onclick="window.currentPhilosophyScene?.selectTagFromSuggestion('${tag}')" style="
                padding: 10px 12px;
                cursor: pointer;
                color: rgba(255, 255, 255, 0.9);
                font-size: 14px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                transition: background 0.2s ease;
            " onmouseover="this.style.background='rgba(0, 212, 255, 0.1)'" onmouseout="this.style.background='transparent'">
                🏷️ ${tag}
            </div>
        `).join('');
        
        suggestionsDiv.style.display = 'block';
    }
    
    selectTagFromSuggestion(tagName) {
        this.addTag(tagName);
        const tagInput = document.getElementById('tagInput');
        if (tagInput) {
            tagInput.value = '';
            tagInput.focus();
        }
        const suggestions = document.getElementById('tagSuggestions');
        if (suggestions) suggestions.style.display = 'none';
    }

    selectCategory(category) {
        console.log('🎭 Desktop Philosophy: Category selected:', category);
        this.sessionData.selectedCategory = category;
        this.currentStep = PHILOSOPHY_FLOW_STEPS.EMOTION_SELECTION;
        this.updateStepContent();
    }

    selectEmotion(emotion) {
        console.log('🎭 Desktop Philosophy: Emotion selected:', emotion);
        this.sessionData.selectedEmotion = emotion;
        this.currentStep = PHILOSOPHY_FLOW_STEPS.COLOR_SELECTION;
        this.updateStepContent();
    }

    selectColor(colorData) {
        console.log('🎭 Desktop Philosophy: Color selected:', colorData);
        this.sessionData.selectedColor = colorData;
        this.currentStep = PHILOSOPHY_FLOW_STEPS.SLEEP_SELECTION;
        this.updateStepContent();
    }
    
    selectSleep(sleepHours) {
        console.log('🎭 Desktop Philosophy: Sleep selected:', sleepHours);
        this.sessionData.selectedSleep = { hours: sleepHours };
        // Selection complete - show the selected sleep
        const selectedSleepEl = document.getElementById('selectedSleepHours');
        if (selectedSleepEl) {
            selectedSleepEl.textContent = `${sleepHours} hours`;
        }
        const continueBtn = document.querySelector('[data-action="generate"]');
        if (continueBtn) {
            continueBtn.disabled = false;
            continueBtn.style.opacity = '1';
        }
    }
    
    createSleepSelectionHTML() {
        return `
            <div class="step-content" style="text-align: center; width: 100%;">
                <h1 style="
                    font-size: 48px;
                    font-weight: 700;
                    margin-bottom: 16px;
                    background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                ">Rest & Recovery</h1>
                
                <p style="
                    font-size: 20px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 48px;
                ">How many hours of sleep did you get last night?</p>
                
                <div class="sleep-options" style="
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                    max-width: 700px;
                    margin: 0 auto 32px auto;
                ">
                    ${['< 4', '4-5', '6-7', '8+'].map(hours => `
                        <div class="sleep-option" data-sleep="${hours}" style="
                            background: rgba(30, 41, 59, 0.8);
                            backdrop-filter: blur(20px);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 16px;
                            padding: 24px 16px;
                            cursor: pointer;
                            transition: all 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
                            text-align: center;
                        ">
                            <div style="font-size: 32px; margin-bottom: 12px;">💤</div>
                            <div style="font-size: 18px; font-weight: 600; color: rgba(255, 255, 255, 0.9);">${hours}</div>
                            <div style="font-size: 12px; color: rgba(255, 255, 255, 0.6); margin-top: 4px;">hours</div>
                        </div>
                    `).join('')}
                </div>
                
                <div style="margin-top: 32px; min-height: 60px;">
                    <div id="selectedSleepHours" style="
                        font-size: 16px;
                        color: #00d4ff;
                        font-weight: 600;
                        margin-bottom: 16px;
                    "></div>
                    <button data-action="generate" disabled style="
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        color: #0f172a;
                        padding: 16px 48px;
                        border-radius: 12px;
                        font-size: 18px;
                        font-weight: 700;
                        cursor: pointer;
                        opacity: 0.5;
                        transition: all 0.3s ease;
                        box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
                    ">Generate Your Quote →</button>
                </div>
            </div>
        `;
    }

    updateStepContent() {
        const contentContainer = document.getElementById('philosophy-step-content');
        if (contentContainer) {
            contentContainer.innerHTML = this.getStepContent();
            this.setupEventListeners(); // Re-bind events for new content
        }
        
        // Update progress in sidebar (4 input steps + generating + display = 6 total, but show progress for first 4)
        const progressBar = document.querySelector('.progress-fill-bar');
        if (progressBar) {
            const progressPercent = this.currentStep <= 4 ? (this.currentStep / 4) * 100 : 100;
            progressBar.style.width = `${progressPercent}%`;
        }
        
        // Update step text
        const stepText = document.querySelector('.philosophy-sidebar .progress-section div');
        if (stepText) {
            if (this.currentStep <= 4) {
                stepText.textContent = `Step ${this.currentStep} of 4`;
            } else {
                stepText.textContent = this.currentStep === 5 ? 'Generating...' : 'Complete';
            }
        }
        
        console.log(`🎭 Desktop Philosophy: Updated to step ${this.currentStep}`);
    }

    async handleAction(action) {
        console.log('🎭 Desktop Philosophy: Handling action:', action);
        
        switch (action) {
            case 'back':
                if (this.currentStep > 1) {
                    this.currentStep--;
                    this.updateStepContent();
                } else {
                    this.returnToDashboard();
                }
                break;
            case 'generate':
                if (this.sessionData.selectedColor) {
                    await this.generateInsight();
                }
                break;
            case 'dashboard':
                this.returnToDashboard();
                break;
        }
    }

    async generateInsight() {
        this.currentStep = PHILOSOPHY_FLOW_STEPS.GENERATING;
        this.updateStepContent();
        
        try {
            const prompt = this._buildPhilosophicalPrompt();
            const response = await this.claudeService.callClaudeApiWithRetry(prompt, 250, 2);
            const extractedText = this.claudeService.extractResponse(response);
            
            console.log('🎭 Desktop Philosophy: Raw Claude response:', extractedText);
            
            // Parse response using same logic as original Philosophy Flow
            const quoteMatch = extractedText.match(/QUOTE:\s*(.*?)\s*FIGURE:/i);
            const figureMatch = extractedText.match(/FIGURE:\s*(.*?)\s*REASONING:/i);
            const reasoningMatch = extractedText.match(/REASONING:\s*(.*?)$/i);

            const quoteData = {
                quote: quoteMatch ? quoteMatch[1].trim().replace(/^"|"$/g, '') : "The obstacle is the way.",
                author: figureMatch ? figureMatch[1].trim() : "Marcus Aurelius",
                context: reasoningMatch ? reasoningMatch[1].trim() : "Universal wisdom for strategic thinking."
            };
            
            console.log('🎭 Desktop Philosophy: Parsed quote data:', quoteData);
            
            if (quoteData.quote && quoteData.author) {
                this.sessionData.quoteData = quoteData;
                this.currentStep = PHILOSOPHY_FLOW_STEPS.DISPLAY_QUOTE;
                this.updateStepContent();
                await this._saveQuoteTimestamp();
            } else {
                throw new Error('Failed to parse quote data');
            }
        } catch (error) {
            console.error('Error generating philosophical quote:', error);
            // Use fallback quote
            this.sessionData.quoteData = {
                quote: "The obstacle is the way.",
                author: "Marcus Aurelius", 
                context: "When facing challenges, remember that obstacles often reveal the path forward."
            };
            this.currentStep = PHILOSOPHY_FLOW_STEPS.DISPLAY_QUOTE;
            this.updateStepContent();
        }
    }

    returnToDashboard() {
        templateOverlayManager.prepareForTransition('AuthenticatedMenuSceneDOM');
        this.startScene('AuthenticatedMenuSceneDOM');
    }
    
    // Journal/Notes functionality methods
    toggleFavorite() {
        const action = document.getElementById('favoriteAction');
        
        if (this.isFavorited) {
            action.classList.remove('active');
            action.querySelector('.action-label').textContent = 'Favorite';
            this.isFavorited = false;
        } else {
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Favorited ✓';
            this.isFavorited = true;
        }
        
        console.log(`🎭 Favorite toggled: ${this.isFavorited}`);
    }
    
    openExecutiveJournal() {
        const journalInterface = document.getElementById('journalInterface');
        const action = document.getElementById('journalAction');
        
        if (journalInterface.classList.contains('active')) {
            this.cancelJournal();
        } else {
            journalInterface.classList.add('active');
            journalInterface.style.display = 'block';
            setTimeout(() => journalInterface.style.opacity = '1', 10);
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Writing...';
            document.getElementById('executiveJournal').focus();
            
            // CRITICAL FIX: Setup tag input listeners NOW (after journal is visible)
            this.setupTagInputListeners();
        }
    }
    
    setupTagInputListeners() {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
            const tagInput = document.getElementById('tagInput');
            if (!tagInput) {
                console.warn('⚠️ Tag input not found - cannot setup listeners');
                return;
            }
            
            console.log('🏷️ Setting up tag input listeners on:', tagInput);
            
            // Handle Enter key to add tag
            tagInput.addEventListener('keydown', (e) => {
                console.log('🏷️ Keydown event fired:', e.key);
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = e.target.value.trim();
                    console.log('🏷️ Enter pressed, value:', value);
                    if (value) {
                        this.addTag(value);
                        e.target.value = '';
                        console.log('🏷️ Tag added via Enter key:', value);
                    }
                }
            });
            
            // Show suggestions as user types
            tagInput.addEventListener('input', (e) => {
                console.log('🏷️ Input event:', e.target.value);
                this.showTagSuggestions(e.target.value);
            });
            
            // Hide suggestions when clicking outside
            tagInput.addEventListener('blur', () => {
                setTimeout(() => {
                    const suggestions = document.getElementById('tagSuggestions');
                    if (suggestions) suggestions.style.display = 'none';
                }, 200);
            });
            
            console.log('✅ Tag input listeners setup complete on element:', tagInput.id);
        }, 100);
    }
    
    cancelJournal() {
        const journalInterface = document.getElementById('journalInterface');
        const action = document.getElementById('journalAction');
        
        journalInterface.style.opacity = '0';
        setTimeout(() => {
            journalInterface.classList.remove('active');
            journalInterface.style.display = 'none';
        }, 300);
        action.classList.remove('active');
        action.querySelector('.action-label').textContent = 'Executive Notes';
        document.getElementById('executiveJournal').value = '';
        this.updateCharacterCount();
    }
    
    async saveJournal() {
        const input = document.getElementById('executiveJournal');
        const action = document.getElementById('journalAction');
        
        if (!input || input.value.trim().length === 0) {
            alert('Please document your strategic insights before saving');
            return;
        }
        
        this.executiveNote = input.value.trim();
        console.log('🎭 Executive note saved to memory:', this.executiveNote);
        console.log('🏷️ Current selectedTags before save:', this.selectedTags);
        console.log('🏷️ selectedTags array length:', this.selectedTags?.length);
        
        // CRITICAL FIX: Automatically save to Firebase so note appears in Library
        action.querySelector('.action-label').textContent = 'Saving...';
        const saved = await this.saveQuoteToFirebase();
        
        if (saved) {
            action.querySelector('.action-label').textContent = 'Saved to Library ✓';
            console.log('✅ Note saved to Firebase - will appear in My Library');
        } else {
            action.querySelector('.action-label').textContent = 'Save Failed';
            console.error('❌ Failed to save note to Firebase');
        }
        
        setTimeout(() => {
            this.cancelJournal();
        }, 1500);
    }
    
    updateCharacterCount() {
        const input = document.getElementById('executiveJournal');
        const count = document.getElementById('charCount');
        
        if (input && count) {
            count.textContent = input.value.length;
            
            // Update color based on length
            if (input.value.length > 450) {
                count.style.color = '#f59e0b'; // Warning color
            } else if (input.value.length === 500) {
                count.style.color = '#ef4444'; // Error color
            } else {
                count.style.color = 'rgba(255, 255, 255, 0.6)';
            }
        }
    }
    
    async saveToLibrary() {
        const action = document.getElementById('saveAction');
        
        console.log('🎭 Saving quote to library...');
        
        const saved = await this.saveQuoteToFirebase();
        
        if (saved) {
            this.isSaved = true;
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Saved ✓';
            console.log('✅ Quote saved to library');
        } else {
            console.warn('Failed to save quote to library');
        }
    }
    
    async saveQuoteToFirebase() {
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        
        if (!user || (!user.emailVerified && !specialAccess)) {
            console.log('🎭 User not authenticated, skipping Firebase save');
            alert('Please sign in to save quotes to your library');
            return false;
        }
        
        try {
            // Validate we have quote data
            if (!this.sessionData.quoteData || !this.sessionData.quoteData.quote) {
                console.error('🎭 Cannot save - no quote data available');
                alert('Please generate a quote first before saving notes.');
                return false;
            }
            
            const quoteDoc = {
                userId: user.uid,
                userEmail: user.email,
                quote: this.sessionData.quoteData.quote,
                figure: this.sessionData.quoteData.author || 'Unknown', // Firebase rules expect 'figure' not 'author'
                reasoning: this.sessionData.quoteData.context || '', // Use 'reasoning' to match rules
                selectedCategory: this.sessionData.selectedCategory,
                selectedEmotion: this.sessionData.selectedEmotion,
                selectedColor: this.sessionData.selectedColor,
                selectedSleep: this.sessionData.selectedSleep, // Sleep hours selection
                favorited: this.isFavorited,
                saved: this.isSaved,
                executiveNote: this.executiveNote || '',
                tags: this.selectedTags || [], // User-defined tags
                tagsLowercase: (this.selectedTags || []).map(t => t.toLowerCase()), // For case-insensitive search
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                sessionType: 'Philosophy Flow Desktop',
                sessionId: this.sessionData.sessionId
            };
            
            console.log('🎭 Saving quote to Firebase with tags:', quoteDoc);
            console.log('🏷️ Tags being saved:', this.selectedTags);
            const docRef = await db.collection('philosophyQuotes').add(quoteDoc);
            console.log('✅ Quote saved successfully:', docRef.id);
            console.log('✅ Saved quote data:', quoteDoc);
            return true;
        } catch (error) {
            console.error('❌ Error saving quote to Firebase:', error);
            console.error('❌ Error details:', error.message);
            console.error('❌ Error code:', error.code);
            alert(`Error saving quote: ${error.message}. Please check console for details.`);
            return false;
        }
    }
    
    startNewSession() {
        console.log('🎭 Starting new Philosophy Flow session...');
        
        // Reset all session data
        this.currentStep = PHILOSOPHY_FLOW_STEPS.CATEGORY_SELECTION;
        this.sessionData = {
            selectedCategory: null,
            selectedEmotion: null,
            selectedColor: null,
            selectedSleep: null,
            quoteData: null,
            sessionId: this._generateSessionId()
        };
        this.executiveNote = '';
        this.isFavorited = false;
        this.isSaved = false;
        this.selectedTags = [];
        
        // Refresh the interface
        this.updateStepContent();
        
        console.log('✨ New session started - ready for insights');
    }
    
    discardSession() {
        if (confirm('Discard this Philosophy Flow session? Your insights will not be saved.')) {
            console.log('🎭 Session discarded');
            this.returnToDashboard();
        }
    }
    
    async completeSession() {
        console.log('🎭 Completing Philosophy Flow session...');
        
        // Save to Firebase if user is authenticated
        await this.saveQuoteToFirebase();
        
        // Show completion message
        const completeBtn = document.getElementById('completeSession');
        if (completeBtn) {
            completeBtn.textContent = 'Session Completed ✓';
            completeBtn.style.background = 'rgba(16, 185, 129, 0.3)';
            completeBtn.style.borderColor = '#10b981';
            completeBtn.style.color = '#10b981';
        }
        
        setTimeout(() => {
            this.returnToDashboard();
        }, 2000);
    }

    // Helper methods (copied from mobile version)
    async _loadUserState() {
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        
        if (user) {
            try {
                const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
                if (profileDoc.exists) {
                    const profileData = profileDoc.data();
                    this.lastQuoteTimestamp = profileData.lastPhilosophyQuoteTimestamp || null;
                    this.isAdmin = profileData.role === 'admin';
                    
                    if (!this.isAdmin && this.lastQuoteTimestamp) {
                        this._checkRateLimit();
                    }
                }
                
                // Load user's existing tags
                await this._loadExistingTags();
                
            } catch (error) {
                console.warn('Error loading user state:', error);
            }
        } else if (specialAccess) {
            this.isAdmin = true;
        }
        
        console.log(`✅ User state loaded - Admin: ${this.isAdmin}, Rate Limited: ${this.isRateLimited}`);
    }
    
    async _loadExistingTags() {
        try {
            const user = auth.currentUser;
            if (!user) return;
            
            // Get all user's quotes and extract unique tags
            const quotesSnapshot = await db.collection('philosophyQuotes')
                .where('userId', '==', user.uid)
                .get();
            
            const tagsSet = new Set();
            quotesSnapshot.forEach(doc => {
                const tags = doc.data().tags || [];
                tags.forEach(tag => tagsSet.add(tag));
            });
            
            this.availableTags = Array.from(tagsSet).sort();
            console.log(`🏷️ Loaded ${this.availableTags.length} existing tags:`, this.availableTags);
            
        } catch (error) {
            console.warn('Could not load existing tags:', error);
            this.availableTags = [];
        }
    }

    _checkRateLimit() {
        if (!this.lastQuoteTimestamp) return;
        
        const now = new Date();
        const lastQuote = this.lastQuoteTimestamp.toDate();
        const timeDiff = now - lastQuote;
        const hoursSinceLastQuote = timeDiff / (1000 * 60 * 60);
        
        this.isRateLimited = hoursSinceLastQuote < 24;
        
        if (this.isRateLimited) {
            this.currentStep = PHILOSOPHY_FLOW_STEPS.RATE_LIMITED;
        }
    }

    _buildPhilosophicalPrompt() {
        const { selectedCategory, selectedEmotion, selectedColor } = this.sessionData;
        
        const category = selectedCategory || 'positive-high';
        const emotion = selectedEmotion || 'Excited';
        const color = selectedColor || { name: 'DeckOps Blue', mood: 'Intelligence' };
        
        return `You are a philosophical advisor for C-suite executives. Generate a meaningful quote for someone feeling ${emotion} (${category} category) with ${color.name} energy representing ${color.mood}.

Format your response EXACTLY like this:
QUOTE: [A meaningful philosophical quote relevant to their emotional state]
FIGURE: [The author or philosopher who said it]
REASONING: [Brief explanation of why this quote is relevant for an executive in this emotional state]

Keep the quote inspirational but grounded, suitable for strategic business thinking.`;
    }

    async _saveQuoteTimestamp() {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            await db.collection("userProfiles").doc(user.uid).update({
                lastPhilosophyQuoteTimestamp: firebase.firestore.FieldValue.serverTimestamp()
            });
            console.log('✅ Quote timestamp saved');
        } catch (error) {
            console.warn('Failed to save quote timestamp:', error);
        }
    }

    _generateSessionId() {
        return `pf_desktop_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    }

    cleanup() {
        console.log('🖥️ PhilosophyFlowDesktopScene: Cleaning up...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        const overlay = document.getElementById('auth-menu-dom-overlay');
        if (overlay) {
            overlay.remove();
        }
        
        // Clean up template overlay manager
        templateOverlayManager.prepareForTransition(null);
    }

    shutdown() {
        console.log('🖥️ PhilosophyFlowDesktopScene: Shutting down...');
        
        this.cleanup();
        super.shutdown();
    }
}
